import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Avatar from 'components/common/Avatar';
import { AccountIcon, AccountIconName } from 'components/account/AccountIcons';
import {
  readLastWorkspace,
  workspacePath,
} from 'components/account/accountRoutes';
import { MeResponse } from 'components/login/types';
import { UserContext } from 'contexts/user';
import {
  getCommunityPath,
  getCommunityProfilePageUrl,
  getForumPath,
  getHelpCenterPath,
} from 'lib/community';
import { insertIf, join } from 'lib/helpers';
import styles from './SiteHeader.module.css';

/** The header title, which opens a menu to move between areas of the site. */
export function PlacesMenu({ navTitle }: { navTitle: string }) {
  const { open, setOpen, ref, triggerRef } = useDropdown();

  return (
    <div ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.Title}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {navTitle}
        <AccountIcon name="chevronDown" size={10} strokeWidth={3} />
      </button>
      {open && (
        <div
          className={join([styles.Panel, styles.PlacesPanel])}
          onClick={() => setOpen(false)}
        >
          <MenuSection label="Go to">
            <MenuLink
              href={getCommunityPath()}
              icon="grid"
              current={navTitle === 'Marketplace'}
              trailing={navTitle === 'Marketplace' && <CurrentCheck />}
            >
              Marketplace
            </MenuLink>
            <MenuLink
              href="/account"
              icon="general"
              current={navTitle === 'Account'}
              trailing={navTitle === 'Account' && <CurrentCheck />}
            >
              Account
            </MenuLink>
          </MenuSection>
          <MenuSection label="Rive">
            <MenuLink
              href="/"
              icon="info"
              external
              trailing={<ExternalArrow />}
            >
              About
            </MenuLink>
            <MenuLink
              href={getForumPath()}
              icon="chat"
              external
              trailing={<ExternalArrow />}
            >
              Community
            </MenuLink>
            <MenuLink
              href={getHelpCenterPath()}
              icon="doc"
              external
              trailing={<ExternalArrow />}
            >
              Docs
            </MenuLink>
          </MenuSection>
        </div>
      )}
    </div>
  );
}

const yourAccountPaths = {
  profile: '/account/profile',
  notifications: '/account/notifications',
  embedUrls: '/account/embed-urls',
};

/** The avatar button and its menu: the user's own account pages. */
export function AccountMenu({ user }: { user: MeResponse }) {
  const { logout } = useContext(UserContext);
  const { pathname } = useRouter();
  const { open, setOpen, ref, triggerRef } = useDropdown();

  const notificationCount = user.notificationCount ?? 0;
  const onYourAccountPage = Object.values(yourAccountPaths).includes(pathname);
  const lastWorkspaceId = open ? readLastWorkspace() : null;

  return (
    <div ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className={join([
          styles.AvatarButton,
          ...insertIf(onYourAccountPage, styles.AvatarButtonCurrent),
        ])}
        aria-label={
          notificationCount > 0
            ? `Your account, ${notificationCount} new notifications`
            : 'Your account'
        }
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        data-testid="user-menu"
      >
        <span className={styles.Avatar}>
          <Avatar data={user} />
        </span>
        {notificationCount > 0 && (
          <span className={styles.NotificationDot} aria-hidden="true" />
        )}
      </button>
      {open && (
        <div
          className={join([styles.Panel, styles.AccountPanel])}
          onClick={() => setOpen(false)}
        >
          <div className={styles.User}>
            <span className={styles.UserAvatar}>
              <Avatar data={user} />
            </span>
            <span className={styles.UserText}>
              <span className={styles.UserName}>
                {user.name ?? user.username}
              </span>
              <span className={styles.UserEmail}>{user.email}</span>
            </span>
          </div>
          {user.username && (
            <Link
              href={getCommunityProfilePageUrl(user.username)}
              className={styles.ProfileButton}
            >
              View public profile
              <AccountIcon name="external" size={14} strokeWidth={2} />
            </Link>
          )}
          <MenuSection label="Your account">
            <MenuLink
              href={yourAccountPaths.profile}
              icon="user"
              current={pathname === yourAccountPaths.profile}
            >
              Profile
            </MenuLink>
            <MenuLink
              href={yourAccountPaths.notifications}
              icon="bell"
              current={pathname === yourAccountPaths.notifications}
              trailing={
                notificationCount > 0 && (
                  <span className={styles.Count}>{notificationCount}</span>
                )
              }
            >
              Notifications
            </MenuLink>
            <MenuLink
              href={yourAccountPaths.embedUrls}
              icon="link"
              current={pathname === yourAccountPaths.embedUrls}
            >
              Embed URLs
            </MenuLink>
          </MenuSection>
          <div className={styles.Section}>
            <MenuLink
              href={
                lastWorkspaceId
                  ? workspacePath(lastWorkspaceId)
                  : '/account/workspaces'
              }
              icon="menu"
            >
              Workspace settings
            </MenuLink>
            <button type="button" className={styles.MenuItem} onClick={logout}>
              <AccountIcon name="signOut" />
              <span className={styles.MenuItemLabel}>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══ Private ═══

/** Open state for a menu that closes on a click outside it or Escape. */
function useDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: PointerEvent) {
      if (!ref.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return { open, setOpen, ref, triggerRef };
}

function MenuSection({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <nav aria-label={label} className={styles.Section}>
      <div className={styles.SectionLabel}>{label}</div>
      {children}
    </nav>
  );
}

function MenuLink({
  href,
  icon,
  current = false,
  external = false,
  trailing,
  children,
}: {
  href: string;
  icon: AccountIconName;
  current?: boolean;
  external?: boolean;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  const className = join([
    styles.MenuItem,
    ...insertIf(current, styles.MenuItemCurrent),
  ]);
  const content = (
    <>
      <AccountIcon name={icon} />
      <span className={styles.MenuItemLabel}>{children}</span>
      {trailing}
    </>
  );

  if (external) {
    return (
      <a href={href} className={className}>
        {content}
      </a>
    );
  }
  return (
    <Link
      href={href}
      aria-current={current ? 'page' : undefined}
      className={className}
    >
      {content}
    </Link>
  );
}

function CurrentCheck() {
  return (
    <span className={styles.Check}>
      <AccountIcon name="check" size={14} strokeWidth={2.2} />
    </span>
  );
}

function ExternalArrow() {
  return (
    <span className={styles.External}>
      <AccountIcon name="external" size={12} strokeWidth={2} />
    </span>
  );
}
